import type { StorePlanningDay } from '@gozoki/api-types'
import { useEffect, useRef } from 'react'
import { PlanningDayColumn } from './PlanningDayColumn'
import {
  DAY_HEADER_HEIGHT,
  GRID_HEIGHT,
  GRID_TOP,
  HOUR_LABELS,
  ROW_HEIGHT,
  yOf,
} from './planning-constants'

// Rail des heures (Figma) : largeur + centrage de chaque libellé sur sa ligne.
const HOUR_RAIL_WIDTH = 29
const HOUR_LABEL_HALF_HEIGHT = 6

/**
 * Grille hebdomadaire du planning : rail d'heures + 7 colonnes jour, défilement vertical avec
 * en-têtes de jour (`sticky` dans chaque colonne) et bandeau du rail épinglés en haut.
 *
 * Partagée par le widget `PlanningWeekCard` et la modale `PlanningWeekModal` — même charte. Le fond
 * du bandeau épinglé est `#F0EDE2`, couleur commune à la carte du widget et au panneau de la modale.
 */
export function PlanningGrid({
  days,
  affluenceMax,
  centerOnNow = false,
}: {
  days: StorePlanningDay[]
  affluenceMax: number
  // Au montage (reload de la page), centre le défilement sur l'heure courante « dans la mesure du
  // possible » : le scrollTop est borné à [0, max], donc tôt le matin épingle le haut de la grille
  // et tard le soir le bas. Une seule fois — pas de recentrage live pendant la session.
  centerOnNow?: boolean
}) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!centerOnNow) return
    const el = scrollRef.current
    if (!el) return
    const now = new Date()
    const y = yOf(now.getHours() * 60 + now.getMinutes())
    // Ligne « heure courante » ramenée au centre du viewport, clampée aux bornes de défilement.
    const target = y - el.clientHeight / 2
    el.scrollTop = Math.max(0, Math.min(target, el.scrollHeight - el.clientHeight))
  }, [centerOnNow])

  return (
    <div
      ref={scrollRef}
      className="flex min-h-0 min-w-0 flex-1 gap-[16px] overflow-y-auto kiosk-scroll-y"
    >
      <div className="flex shrink-0 flex-col" style={{ width: HOUR_RAIL_WIDTH }}>
        <div
          className="sticky top-0 z-10 shrink-0 bg-[#F0EDE2]"
          style={{ height: DAY_HEADER_HEIGHT }}
        />
        <div className="relative shrink-0" style={{ height: GRID_HEIGHT }}>
          {HOUR_LABELS.map((label, index) => (
            <span
              key={label}
              className="absolute left-0 font-sans text-[10px] leading-[12px] text-[#152013] opacity-60"
              style={{ top: GRID_TOP + index * ROW_HEIGHT - HOUR_LABEL_HALF_HEIGHT }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 gap-[4px]">
        {days.map((day) => (
          <PlanningDayColumn key={day.date} day={day} affluenceMax={affluenceMax} />
        ))}
      </div>
    </div>
  )
}
